import { db } from '../firebase';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { MOUNTAIN_LISTS, CROSS_LIST_MAP } from '../data/mountainData';

// In-memory cache for mountain list data (static, fetched once)
const _listCache = {};

export const mountainLogService = {
  // Firestoreに山データが存在しない場合に初期化する
  async initializeMountainData() {
    const promises = Object.entries(MOUNTAIN_LISTS).map(async ([listId, listData]) => {
      const ref = doc(db, 'mountainLists', listId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, {
          name: listData.name,
          shortName: listData.shortName,
          mountains: listData.mountains,
        });
      }
    });
    await Promise.all(promises);
  },

  // 山リストデータを取得（Firestoreを試み、失敗時はローカルデータで返す）
  async getMountainList(listId) {
    if (_listCache[listId]) return _listCache[listId];

    try {
      const ref = doc(db, 'mountainLists', listId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        _listCache[listId] = snap.data();
        return _listCache[listId];
      }
      // データ未存在の場合は非同期でシーディングを試みる（失敗しても無視）
      mountainLogService.initializeMountainData().catch(() => {});
    } catch {
      // 権限エラー等はローカルデータにフォールバック
    }

    _listCache[listId] = {
      name: MOUNTAIN_LISTS[listId].name,
      shortName: MOUNTAIN_LISTS[listId].shortName,
      mountains: MOUNTAIN_LISTS[listId].mountains,
    };
    return _listCache[listId];
  },

  // ユーザーの特定リストの登頂記録を取得
  async getUserLogs(userId, listId) {
    try {
      const logsRef = collection(db, 'users', userId, 'mountainLogs');
      const q = query(logsRef, where('listId', '==', listId));
      const snap = await getDocs(q);
      const logs = {};
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        logs[data.no] = data;
      });
      return logs;
    } catch {
      return {};
    }
  },

  // 登頂記録を保存（merge）
  async saveLog(userId, listId, no, logData) {
    const logId = `${listId}_${no}`;
    const ref = doc(db, 'users', userId, 'mountainLogs', logId);
    await setDoc(
      ref,
      {
        listId,
        no,
        ...logData,
        updatedAt: new Date(),
      },
      { merge: true }
    );
  },

  // 登頂記録を削除
  async deleteLog(userId, listId, no) {
    const logId = `${listId}_${no}`;
    const ref = doc(db, 'users', userId, 'mountainLogs', logId);
    await deleteDoc(ref);
  },

  /**
   * 登頂チェックをクロスリスト同期付きで保存する
   * - climbed=true  → 同名山が存在する他リストにも climbed:true を保存
   * - climbed=false → 同名山が存在する他リストの記録も削除
   * 登頂日・メモはリストごとに独立して管理（同期しない）
   *
   * @returns {string[]} 同期したリストIDの配列（UI更新用）
   */
  async saveClimbedWithSync(userId, listId, no, mountainName, climbed) {
    // 自リストを保存/削除
    if (climbed) {
      await mountainLogService.saveLog(userId, listId, no, { climbed: true });
    } else {
      await mountainLogService.deleteLog(userId, listId, no);
    }

    // 他リストの同名山を同期
    const crossEntries = (CROSS_LIST_MAP[mountainName] || []).filter(
      (e) => !(e.listId === listId && e.no === no)
    );

    await Promise.all(
      crossEntries.map((entry) =>
        climbed
          ? mountainLogService.saveLog(userId, entry.listId, entry.no, { climbed: true })
          : mountainLogService.deleteLog(userId, entry.listId, entry.no)
      )
    );

    return crossEntries.map((e) => e.listId);
  },
};
